import { Check } from "lucide-react";

import { Link } from "@/lib/navigation";
import { useTranslations } from "@repo/internationalization";

// Views that have their own copy in Auth.panel (excludes sign-up which uses default)
const VIEWS_WITH_OWN_COPY = ["sign-in", "forgot-password", "reset-password", "sign-out"] as const;

type ViewWithOwnCopy = (typeof VIEWS_WITH_OWN_COPY)[number];

const hasOwnCopy = (value: string): value is ViewWithOwnCopy =>
  (VIEWS_WITH_OWN_COPY as readonly string[]).includes(value);

/**
 * Always-dark split-panel brand surface for the auth screens. The grid + radial
 * glow echo the Effect ecosystem aesthetic; this surface intentionally ignores
 * the app theme. Copy adapts to the active `[authView]` segment.
 */
export function BrandPanel({ pathname }: { readonly pathname: string }) {
  const t = useTranslations("Auth.panel");

  // sign-up uses the default copy; unknown views also fall back to default
  const view: "default" | ViewWithOwnCopy = hasOwnCopy(pathname) ? pathname : "default";

  const features = [t("features.typeSafe"), t("features.synced"), t("features.private")] as const;

  return (
    <aside className="relative hidden flex-col justify-between overflow-hidden bg-[#0a0a0c] p-12 text-zinc-100 lg:flex">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.35] [background-image:linear-gradient(rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.08)_1px,transparent_1px)] [background-size:44px_44px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-1/3 left-1/4 h-[42rem] w-[42rem] rounded-full bg-[radial-gradient(circle,rgba(167,139,250,0.22),transparent_62%)] blur-2xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 right-0 h-[28rem] w-[28rem] rounded-full bg-[radial-gradient(circle,rgba(217,70,239,0.16),transparent_60%)] blur-2xl"
      />

      <Link
        className="group relative flex w-fit items-center gap-2 font-mono text-xs lowercase tracking-[0.04em] text-zinc-400 transition-colors hover:text-zinc-200"
        href="/"
      >
        <span className="size-1.5 rounded-full bg-violet-400 transition-transform group-hover:scale-125" />
        albertonline.org
      </Link>

      <div className="relative max-w-md">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-violet-300/80">
          {t(`${view}.eyebrow`)}
        </p>
        <h1 className="mt-3 text-balance text-4xl font-semibold leading-tight tracking-tight text-zinc-50">
          {t(`${view}.heading`)}
        </h1>
        <p className="mt-4 text-pretty leading-relaxed text-zinc-400">{t(`${view}.body`)}</p>

        <ul className="mt-8 space-y-3">
          {features.map((feature) => (
            <li className="flex items-center gap-3 text-sm text-zinc-300" key={feature}>
              <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-violet-300">
                <Check aria-hidden="true" className="size-3" />
              </span>
              {feature}
            </li>
          ))}
        </ul>
      </div>

      <p className="relative font-mono text-xs text-zinc-600">type-safe · built on Effect</p>
    </aside>
  );
}
